// Reads a validity interval out of a relationship's `qualifier`.
//
// The extraction prompt tells the model to copy any temporal scope into
// qualifier verbatim — "since the 2021 coup", "during the July offensive" —
// and explicitly forbids it from inferring or estimating dates. So the
// qualifier is un-normalised valid time that has already been extracted and
// already been reviewed. This turns it into dates.
//
// TWO THINGS THIS DELIBERATELY DOES NOT DO.
//
// It does not guess. Every rule requires an explicit temporal preposition
// immediately before a year, because the qualifier is dual-purpose: on a
// speech-act edge it carries what was ALLEGED ("of arming the Rapid Support
// Forces"), not when. "under the 2019 accord" names a document, not a date,
// which is why "under" is not a trigger word.
//
// It does not write. The result is a suggestion the editor accepts or ignores
// in the review panel — the whole design rests on no date entering the graph
// without a human having looked at it.

export type ParsedPrecision = "day" | "month" | "year";

export interface ParsedValidity {
  /** ISO date, start of the matched unit. */
  validFrom: string | null;
  /** ISO date, end of the matched unit. NULL for an open-ended "since". */
  validTo: string | null;
  /**
   * The coarsest precision among the matched phrases. Coarsest, not finest:
   * claiming month accuracy because the END of a range was given to the month
   * would overstate a start that was only given to the year.
   */
  precision: ParsedPrecision;
  /** The substring that produced this, shown to the editor as justification. */
  matched: string;
}

const MONTHS: Record<string, number> = {
  january: 1,
  jan: 1,
  february: 2,
  feb: 2,
  march: 3,
  mar: 3,
  april: 4,
  apr: 4,
  may: 5,
  june: 6,
  jun: 6,
  july: 7,
  jul: 7,
  august: 8,
  aug: 8,
  september: 9,
  sep: 9,
  sept: 9,
  october: 10,
  oct: 10,
  november: 11,
  nov: 11,
  december: 12,
  dec: 12,
};

/** Opens an interval: the phrase says when it started. */
const FROM_PREPS = ["since", "from", "after", "beginning in", "starting in"];
/** Closes an interval: the phrase says when it ended. */
const TO_PREPS = ["until", "till", "up to", "before", "ending in"];
/** Bounds an interval on both sides: the phrase names the whole span. */
const SPAN_PREPS = ["during", "throughout", "in"];

const MONTH_ALT = Object.keys(MONTHS).join("|");
const PREP_ALT = [...FROM_PREPS, ...TO_PREPS, ...SPAN_PREPS]
  .sort((a, b) => b.length - a.length) // longest first: "up to" before "to"
  .join("|");

// <prep> [the] [<Month> [<D>,]] <YYYY>
// The year is required and must be adjacent — that requirement is the entire
// defence against reading "of arming the Rapid Support Forces" as a date.
const PATTERN = new RegExp(
  String.raw`\b(${PREP_ALT})\s+(?:the\s+)?` +
    String.raw`(?:(${MONTH_ALT})\s+(?:(\d{1,2})(?:st|nd|rd|th)?,?\s+)?)?` +
    String.raw`(\d{4})\b`,
  "gi",
);

const PRECISION_RANK: Record<ParsedPrecision, number> = {
  day: 0,
  month: 1,
  year: 2,
};

const iso = (y: number, m: number, d: number): string =>
  `${String(y).padStart(4, "0")}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

/** Day count of a month, via the zeroth day of the next one. */
const lastDay = (y: number, m: number): number =>
  new Date(Date.UTC(y, m, 0)).getUTCDate();

interface Hit {
  kind: "from" | "to" | "span";
  precision: ParsedPrecision;
  start: string;
  end: string;
  matched: string;
}

function readHit(m: RegExpExecArray): Hit | null {
  const prep = m[1].toLowerCase();
  const monthWord = m[2]?.toLowerCase();
  const dayNum = m[3] ? parseInt(m[3], 10) : null;
  const year = parseInt(m[4], 10);

  // A four-digit number outside this range is a quantity, not a year.
  if (year < 1800 || year > 2100) return null;

  const kind: Hit["kind"] = FROM_PREPS.includes(prep)
    ? "from"
    : TO_PREPS.includes(prep)
      ? "to"
      : "span";

  if (monthWord) {
    const month = MONTHS[monthWord];
    if (dayNum != null) {
      if (dayNum < 1 || dayNum > lastDay(year, month)) return null;
      const d = iso(year, month, dayNum);
      return { kind, precision: "day", start: d, end: d, matched: m[0] };
    }
    return {
      kind,
      precision: "month",
      start: iso(year, month, 1),
      end: iso(year, month, lastDay(year, month)),
      matched: m[0],
    };
  }

  return {
    kind,
    precision: "year",
    start: iso(year, 1, 1),
    end: iso(year, 12, 31),
    matched: m[0],
  };
}

export function parseQualifierValidity(
  qualifier: string | null | undefined,
): ParsedValidity | null {
  if (!qualifier || !qualifier.trim()) return null;

  const hits: Hit[] = [];
  PATTERN.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = PATTERN.exec(qualifier)) !== null) {
    const hit = readHit(m);
    if (hit) hits.push(hit);
  }
  if (!hits.length) return null;

  const from = hits.find((h) => h.kind === "from");
  const to = hits.find((h) => h.kind === "to");
  const span = hits.find((h) => h.kind === "span");

  // An explicit endpoint always beats a span: "in 2021" inside a phrase that
  // also says "until 2023" is describing something else.
  const used = [from, to].filter(Boolean) as Hit[];
  if (!used.length && span) used.push(span);
  if (!used.length) return null;

  const precision = used.reduce<ParsedPrecision>(
    (worst, h) =>
      PRECISION_RANK[h.precision] > PRECISION_RANK[worst] ? h.precision : worst,
    "day",
  );

  return {
    validFrom: from ? from.start : span && !to ? span.start : null,
    validTo: to ? to.end : span && !from ? span.end : null,
    precision,
    matched: used.map((h) => h.matched).join(" … "),
  };
}

const MONTH_ABBR = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/**
 * Renders a stored date at the precision it actually has.
 *
 * This is the display half of the honesty lock. A year-precision value is
 * stored as 2021-01-01 because a date column needs a day, and printing it that
 * way would put a fabricated day and month in front of a reader — turning "the
 * article said 2021" into "the article said 1 January 2021". The precision is
 * not decoration on the date; it governs how much of the date may be shown.
 *
 * Split by hand rather than via Date, which would shift the day across a
 * timezone boundary and quietly print the 31st as the 30th.
 */
export function formatValidDate(iso: string, precision: string): string {
  const [y, m, d] = iso.split("-");
  if (precision === "year") return y;
  if (precision === "month") return `${MONTH_ABBR[Number(m) - 1]} ${y}`;
  return `${Number(d)} ${MONTH_ABBR[Number(m) - 1]} ${y}`;
}

/**
 * A validity interval as one short string. An open end reads as "2021 →",
 * which is the article declining to give an end — deliberately distinct from
 * an absent interval, which renders as nothing at all.
 */
export function formatValidity(
  from: string | null,
  to: string | null,
  precision: string,
): string {
  if (from && to) {
    const a = formatValidDate(from, precision);
    const b = formatValidDate(to, precision);
    // At year precision a whole-year span collapses: "2022", not "2022 – 2022".
    return a === b ? a : `${a} – ${b}`;
  }
  if (from) return `${formatValidDate(from, precision)} →`;
  if (to) return `→ ${formatValidDate(to, precision)}`;
  return "";
}
